"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { CheckCircle2, X, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  FINDIN_TASK_NOTIFICATION_EVENT,
  type TaskNotification,
} from "@/lib/task-notifications";
import {
  dismissTaskNotificationToast,
  enqueueTaskNotificationToast,
  getTaskNotificationToastServerSnapshot,
  getTaskNotificationToastSnapshot,
  subscribeTaskNotificationToasts,
} from "@/lib/task-notification-toast-store";

export function TaskNotificationToastBridge() {
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const visibleNotifications = useSyncExternalStore(
    subscribeTaskNotificationToasts,
    getTaskNotificationToastSnapshot,
    getTaskNotificationToastServerSnapshot,
  );

  useEffect(() => {
    setMounted(true);

    const handleTaskNotification = (event: Event) => {
      const notification = (event as CustomEvent<TaskNotification>).detail;
      if (!notification?.title || !notification.body) return;

      if (enqueueTaskNotificationToast(notification)) {
        // publishTaskNotification uses preventDefault as an acknowledgement that
        // the persistent dashboard bridge actually received and queued the event.
        event.preventDefault();
      }
    };

    window.addEventListener(FINDIN_TASK_NOTIFICATION_EVENT, handleTaskNotification);
    return () => {
      window.removeEventListener(FINDIN_TASK_NOTIFICATION_EVENT, handleTaskNotification);
    };
  }, []);

  if (!mounted) return null;

  return createPortal(
    <div
      className="pointer-events-none fixed right-4 top-4 z-[10000] flex flex-col items-end gap-2"
      data-task-notification-toaster
      aria-live="polite"
    >
      {visibleNotifications.map(({ id, notification }) => {
        const succeeded = notification.status === "succeeded";
        const href = notification.href;
        const openNotification = () => {
          if (!href) return;
          router.push(href);
          dismissTaskNotificationToast(id);
        };

        return (
          <div
            key={id}
            className={cn(
              "pointer-events-auto flex w-[min(22rem,calc(100vw-2rem))] items-start gap-3 rounded-lg border bg-background p-4 text-left shadow-lg",
              "animate-in fade-in-0 slide-in-from-top-2",
              href && "cursor-pointer hover:bg-muted/40",
            )}
            data-task-notification-toast
            role={href ? "link" : "status"}
            tabIndex={href ? 0 : undefined}
            onClick={openNotification}
            onKeyDown={(event) => {
              if (href && (event.key === "Enter" || event.key === " ")) {
                event.preventDefault();
                openNotification();
              }
            }}
          >
            {succeeded ? (
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            ) : (
              <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" aria-hidden="true" />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">{notification.title}</p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">{notification.body}</p>
            </div>
            <button
              type="button"
              className="-mr-1 -mt-1 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={(event) => {
                event.stopPropagation();
                dismissTaskNotificationToast(id);
              }}
              aria-label="关闭通知"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>,
    document.body,
  );
}
